import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { DropdownBaseItemProps } from '@hotjar/hotsauce';
import { Button, Divider, SearchInput, tokens } from '@hotjar/hotsauce';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { useNavigation } from '@hotjar/navigation';
import {
  SideBarWrapper,
  SearchInputWrapper,
} from '@hotjar/insights-common/components/SideBar/SideBar';
import { useTranslation } from 'react-i18next';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons';
import { List } from './List';

export type State = 'idle' | 'loading' | 'ready' | 'failed';

export interface SidebarItem {
  id: number;
  name: string;
  isActive?: boolean;
  loading?: boolean;
  isEditing?: boolean;
}

export interface ModalConfig<T> {
  /**
   * Selected to remove item
   */
  itemToRemove: T;
  /**
   * Flag to show if modal is open or not
   */
  isOpen: boolean;
  /**
   * Function to Close Modal and flip isOpen Flag
   */
  onClose?: () => void;
  /**
   * Confirmation function to call the onRemove of the Pinner
   */
  onConfirm?: () => void;
}

export interface SideBarProps<T> {
  itemsList: T[];
  newItemText: string;
  listTitle: string;
  emptyStateText: string;
  errorStateText: string;
  listState: State;
  isReadOnly: boolean;
  renderConfirmationModalComponent: (modalConfig: ModalConfig<T>) => JSX.Element | null;
  onItemClick: (item: T) => void;
  onItemSave: (item: T) => void;
  onItemRemove: (item: T) => void;
  onItemEdit: () => void;
  onNewButtonClick: () => void;
}

export interface SideBarListProps<T> extends SideBarProps<T> {
  currentSite: Site;
}

export interface SidebarItemAction<T> extends Omit<DropdownBaseItemProps, 'children'> {
  name: string;
  label: string;
  icon: keyof IconSetDefinitions;
  cb: (itemId: T) => void;
}

const AddNewButton = styled(Button)`
  margin: ${tokens.spacer.x1} 0 0;
  svg,
  span {
    color: ${tokens.color.link};
  }
`;

const StyledSearchList = styled(List)<{ hasMarginBottom?: boolean }>`
  margin-top: ${tokens.spacer.x2pt5};
` as typeof List;

function searchItem(searchPhrase: string, item: SidebarItem): boolean {
  return item.name.toLocaleLowerCase().includes(searchPhrase);
}

export const SideBarList = <T extends SidebarItem>({
  itemsList,
  newItemText,
  listTitle,
  errorStateText,
  emptyStateText,
  currentSite,
  listState,
  isReadOnly,
  renderConfirmationModalComponent,
  onItemClick,
  onItemSave,
  onItemRemove,
  onItemEdit,
  onNewButtonClick,
}: SideBarListProps<T>): JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const [itemsPinned, setItemsPinned] = useState<T[]>(itemsList);
  const [confirmRemovalOfPin, setConfirmRemovalOfPin] = useState<T | null>(null);
  const { t } = useTranslation('heatmaps');
  const { setIsSidebarMinimized, topBarHeight: topNavHeight } = useNavigation();
  const { getState } = useTrackingCode();
  const isTrackingCodeInstalled = getState(currentSite?.lastData)?.installed;

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const itemsFound =
    search.length === 0 ? [] : itemsPinned.filter((item) => searchItem(search, item));

  const onEdit = (editingItem: T) => {
    setItemsPinned((items) =>
      items.map((item) => ({ ...item, isEditing: item.id === editingItem.id })),
    );
    onItemEdit();
  };

  const onClearEdit = () => {
    setItemsPinned((items) => items.map((item) => ({ ...item, isEditing: false })));
  };

  const pinnedActions: SidebarItemAction<T>[] = [
    {
      name: 'edit',
      label: t('sidebar.rename-action'),
      icon: 'text_field_short',
      cb: onEdit,
    },
    {
      name: 'unpin',
      label: t('sidebar.remove-action'),
      icon: 'trash',
      hasDivider: true,
      kind: 'danger_ghost',
      cb: setConfirmRemovalOfPin,
    },
  ];

  useEffect(() => {
    setItemsPinned(itemsList);
  }, [itemsList]);

  useEffect(() => {
    setIsSidebarMinimized(true);
  }, [setIsSidebarMinimized]);

  return (
    <SideBarWrapper topNavHeight={topNavHeight}>
      <SearchInputWrapper
        css={
          !isTrackingCodeInstalled &&
          css`
            margin-bottom: ${tokens.spacer.x1pt5};
          `
        }
      >
        <SearchInput onChange={onSearch} placeholder={t('sidebar.search-placeholder')} size="s" />
      </SearchInputWrapper>

      {isTrackingCodeInstalled && (
        <>
          <AddNewButton kind="ghost" as="button" icon="plus" onClick={onNewButtonClick}>
            {newItemText}
          </AddNewButton>
          <Divider />
        </>
      )}

      {search.length === 0 ? (
        <List
          title={listTitle}
          items={itemsPinned}
          isReadOnly={isReadOnly}
          errorStateText={errorStateText}
          emptyStateText={emptyStateText}
          onClick={onItemClick}
          onItemSave={onItemSave}
          onItemBlur={onClearEdit}
          state={listState}
          actions={pinnedActions}
        />
      ) : (
        <StyledSearchList
          items={itemsFound}
          errorStateText=""
          emptyStateText={t('sidebar.search-no-results')}
          onClick={onItemClick}
          state="ready"
        />
      )}
      {confirmRemovalOfPin &&
        renderConfirmationModalComponent({
          itemToRemove: confirmRemovalOfPin,
          isOpen: true,
          onClose: () => setConfirmRemovalOfPin(null),
          onConfirm: () => {
            onItemRemove(confirmRemovalOfPin);
            setConfirmRemovalOfPin(null);
          },
        })}
    </SideBarWrapper>
  );
};

export const SideBar = <T extends SidebarItem>({
  itemsList,
  newItemText,
  listTitle,
  errorStateText,
  emptyStateText,
  listState,
  isReadOnly,
  renderConfirmationModalComponent,
  onItemClick,
  onItemSave,
  onItemRemove,
  onItemEdit,
  onNewButtonClick,
}: SideBarProps<T>): JSX.Element | null => {
  const currentSite = useCurrentSite();

  return currentSite.data?.id ? (
    <SideBarList
      itemsList={itemsList}
      newItemText={newItemText}
      listTitle={listTitle}
      errorStateText={errorStateText}
      emptyStateText={emptyStateText}
      currentSite={currentSite.data}
      listState={listState}
      isReadOnly={isReadOnly}
      renderConfirmationModalComponent={renderConfirmationModalComponent}
      onItemClick={onItemClick}
      onItemSave={onItemSave}
      onItemRemove={onItemRemove}
      onItemEdit={onItemEdit}
      onNewButtonClick={onNewButtonClick}
    />
  ) : null;
};
